import { TARGET_DIMS, type Orientation } from "./convert";
import { loadTapStamp } from "./stamp";

type DrawableStamp = ImageBitmap | HTMLCanvasElement;

export type Tool = "pen" | "eraser" | "bucket" | "magic" | "hand";

type MaskOp =
  | { kind: "stroke"; tool: "pen" | "eraser"; size: number; points: number[] }
  | { kind: "bucket"; x: number; y: number }
  | { kind: "magic"; x: number; y: number }
  | { kind: "fillAll" }
  | { kind: "clear" };

interface StampSnapshot {
  x: number;
  y: number;
  scale: number;
}

type HistoryOp =
  | { type: "mask"; op: MaskOp }
  | { type: "stamp"; before: StampSnapshot; after: StampSnapshot };

/**
 * History is command-based: ops are cheap to store and are replayed from the
 * nearest alpha-only keyframe on undo, so memory stays bounded even though the
 * mask canvas is full output resolution (~4 MB per snapshot).
 */
const KEYFRAME_EVERY = 10;
const MAX_HISTORY = 60;
const REBASE_DROP = KEYFRAME_EVERY;

/** Max squared RGB distance for the magic wand's "similar color" test. */
const MAGIC_TOLERANCE_SQ = 48 * 48;

export interface BakeResult {
  rgba: Uint8ClampedArray;
  protectedAlpha: Uint8Array;
  width: number;
  height: number;
}

interface StampState {
  bitmap: DrawableStamp | null;
  x: number;
  y: number;
  scale: number;
  visible: boolean;
}

export interface EditorEvents {
  onChange?: () => void;
  onHistoryChange?: () => void;
}

const MASK_OVERLAY_COLOR = "rgba(0, 119, 182, 0.55)";

export class Editor {
  readonly previewCanvas: HTMLCanvasElement;
  private readonly mask: HTMLCanvasElement;
  private readonly overlay: HTMLCanvasElement;
  private source: HTMLCanvasElement | null = null;
  private sourceBitmap: ImageBitmap | null = null;
  private backdropPattern: CanvasPattern | null = null;

  orientation: Orientation = "portrait";
  maskVisible = true;
  tool: Tool = "pen";
  penSize = 48;
  lineMode = false;

  readonly stamp: StampState = {
    bitmap: null,
    x: 0,
    y: 0,
    scale: 1,
    visible: false,
  };

  private dragging: "stamp" | "paint" | "line" | null = null;
  private pendingLine: { x0: number; y0: number; x1: number; y1: number; tool: "pen" | "eraser" } | null = null;
  /** Last committed line endpoint; the next tap in line mode chains from here. */
  private lineAnchor: { x: number; y: number } | null = null;
  private dragOffset = { x: 0, y: 0 };
  private prevPointer: { x: number; y: number } | null = null;
  private stampMoved = false;
  private readonly events: EditorEvents;

  private history: HistoryOp[] = [];
  private historyIndex = 0;
  private baseMaskAlpha: Uint8Array | null = null;
  private readonly keyframes = new Map<number, Uint8Array>();
  private activeStroke: { tool: "pen" | "eraser"; size: number; points: number[] } | null = null;
  private stampDragBefore: StampSnapshot | null = null;

  constructor(previewCanvas: HTMLCanvasElement, events: EditorEvents = {}) {
    this.previewCanvas = previewCanvas;
    this.mask = document.createElement("canvas");
    this.overlay = document.createElement("canvas");
    this.events = events;
    this.applyOrientation(this.orientation);
    this.captureHistoryBase();
    this.installPointerHandlers();
    this.render();
  }

  get dims(): { w: number; h: number } {
    return TARGET_DIMS[this.orientation];
  }

  hasSource(): boolean {
    return this.source !== null;
  }

  setOrientation(o: Orientation): void {
    if (o === this.orientation) return;
    const oldDims = TARGET_DIMS[this.orientation];
    const newDims = TARGET_DIMS[o];
    const relStampX = oldDims.w > 0 ? this.stamp.x / oldDims.w : 0.5;
    const relStampY = oldDims.h > 0 ? this.stamp.y / oldDims.h : 0.5;
    const relStampWidth =
      this.stamp.bitmap && oldDims.w > 0
        ? (this.stamp.bitmap.width * this.stamp.scale) / oldDims.w
        : 0;
    this.orientation = o;
    this.applyOrientation(o);
    if (this.sourceBitmap) this.applySourceBitmap(this.sourceBitmap);
    this.stamp.x = relStampX * newDims.w;
    this.stamp.y = relStampY * newDims.h;
    if (this.stamp.bitmap && relStampWidth > 0) {
      this.stamp.scale = (relStampWidth * newDims.w) / this.stamp.bitmap.width;
    }
    // Mask was rescaled, so recorded op coordinates no longer apply.
    this.resetHistory();
    this.render();
    this.events.onChange?.();
  }

  private applyOrientation(o: Orientation): void {
    const oldW = this.mask.width;
    const oldH = this.mask.height;

    let savedMask: HTMLCanvasElement | null = null;
    if (oldW > 0 && oldH > 0) {
      savedMask = document.createElement("canvas");
      savedMask.width = oldW;
      savedMask.height = oldH;
      const sctx = savedMask.getContext("2d");
      if (sctx) sctx.drawImage(this.mask, 0, 0);
    }

    const { w, h } = TARGET_DIMS[o];
    this.previewCanvas.width = w;
    this.previewCanvas.height = h;
    this.mask.width = w;
    this.mask.height = h;
    this.overlay.width = w;
    this.overlay.height = h;

    if (savedMask) {
      const mctx = this.mask.getContext("2d");
      if (mctx) mctx.drawImage(savedMask, 0, 0, oldW, oldH, 0, 0, w, h);
    }
  }

  async loadSource(file: Blob): Promise<void> {
    this.sourceBitmap?.close();
    const bm = await createImageBitmap(file, { imageOrientation: "from-image" });
    this.sourceBitmap = bm;
    this.applySourceBitmap(bm);
    // Magic-wand ops replay against source pixels, so a new source invalidates history.
    this.resetHistory();
    this.render();
    this.events.onChange?.();
  }

  private applySourceBitmap(bm: ImageBitmap): void {
    const { w, h } = this.dims;
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    if (!ctx) throw new Error("2d context unavailable");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    const scale = Math.max(w / bm.width, h / bm.height);
    const sw = bm.width * scale;
    const sh = bm.height * scale;
    ctx.drawImage(bm, (w - sw) / 2, (h - sh) / 2, sw, sh);
    this.source = c;
  }

  async ensureStamp(): Promise<void> {
    if (this.stamp.bitmap) return;
    this.stamp.bitmap = await loadTapStamp();
    this.resetStamp();
  }

  setMaskVisible(v: boolean): void {
    this.maskVisible = v;
    this.render();
  }

  async setStampVisible(v: boolean): Promise<void> {
    if (v && !this.stamp.bitmap) await this.ensureStamp();
    this.stamp.visible = v;
    this.render();
    this.events.onChange?.();
  }

  setTool(tool: Tool): void {
    this.tool = tool;
    if (tool !== "pen" && tool !== "eraser") this.cancelLine();
  }

  setPenSize(size: number): void {
    this.penSize = size;
  }

  setLineMode(v: boolean): void {
    this.lineMode = v;
    // Each toggle starts a fresh chain — a leftover anchor would surprise.
    this.cancelLine();
    this.render();
  }

  /** Ends the current polyline chain (Esc, mode off, tool switch). */
  cancelLine(): void {
    if (!this.lineAnchor && !this.pendingLine) return;
    this.lineAnchor = null;
    this.pendingLine = null;
    this.render();
  }

  // ===== undo / redo =====

  get canUndo(): boolean {
    return this.historyIndex > 0;
  }

  get canRedo(): boolean {
    return this.historyIndex < this.history.length;
  }

  undo(): void {
    if (!this.canUndo) return;
    const target = this.historyIndex - 1;
    const op = this.history[target];
    if (op.type === "stamp") {
      this.applyStampSnapshot(op.before);
    } else {
      this.restoreMaskToIndex(target);
    }
    this.historyIndex = target;
    // The chain endpoint may no longer exist on the mask — start fresh.
    this.lineAnchor = null;
    this.pendingLine = null;
    this.render();
    this.events.onChange?.();
    this.events.onHistoryChange?.();
  }

  redo(): void {
    if (!this.canRedo) return;
    const op = this.history[this.historyIndex];
    if (op.type === "stamp") {
      this.applyStampSnapshot(op.after);
    } else {
      this.applyMaskOp(op.op);
    }
    this.historyIndex++;
    this.lineAnchor = null;
    this.pendingLine = null;
    this.render();
    this.events.onChange?.();
    this.events.onHistoryChange?.();
  }

  private resetHistory(): void {
    this.lineAnchor = null;
    this.pendingLine = null;
    this.captureHistoryBase();
    this.events.onHistoryChange?.();
  }

  private captureHistoryBase(): void {
    this.history = [];
    this.historyIndex = 0;
    this.keyframes.clear();
    this.baseMaskAlpha = this.snapshotMaskAlpha();
  }

  private stampSnapshot(): StampSnapshot {
    return { x: this.stamp.x, y: this.stamp.y, scale: this.stamp.scale };
  }

  private applyStampSnapshot(s: StampSnapshot): void {
    this.stamp.x = s.x;
    this.stamp.y = s.y;
    this.stamp.scale = s.scale;
  }

  private snapshotMaskAlpha(): Uint8Array {
    const w = this.mask.width;
    const h = this.mask.height;
    const out = new Uint8Array(w * h);
    if (w === 0 || h === 0) return out;
    const ctx = this.mask.getContext("2d");
    if (!ctx) return out;
    const data = ctx.getImageData(0, 0, w, h).data;
    for (let i = 0; i < out.length; i++) out[i] = data[i * 4 + 3];
    return out;
  }

  private restoreMaskAlpha(alpha: Uint8Array): void {
    const w = this.mask.width;
    const h = this.mask.height;
    const ctx = this.mask.getContext("2d");
    if (!ctx || alpha.length !== w * h) return;
    const id = ctx.createImageData(w, h);
    for (let i = 0; i < alpha.length; i++) {
      id.data[i * 4 + 3] = alpha[i];
    }
    ctx.putImageData(id, 0, 0);
  }

  private pushOp(op: HistoryOp): void {
    this.history.length = this.historyIndex; // drop redo tail
    for (const k of [...this.keyframes.keys()]) {
      if (k > this.historyIndex) this.keyframes.delete(k);
    }
    this.history.push(op);
    this.historyIndex = this.history.length;
    if (this.historyIndex % KEYFRAME_EVERY === 0) {
      this.keyframes.set(this.historyIndex, this.snapshotMaskAlpha());
    }
    while (this.history.length > MAX_HISTORY) this.rebase(REBASE_DROP);
    this.events.onHistoryChange?.();
  }

  /** Drop the oldest `drop` ops, making the state after them the new history base. */
  private rebase(drop: number): void {
    const keyframe = this.keyframes.get(drop);
    if (keyframe) {
      this.baseMaskAlpha = keyframe;
    } else if (this.baseMaskAlpha) {
      // Recompute: replay the dropped ops on a copy of the current base.
      const live = this.snapshotMaskAlpha();
      this.restoreMaskAlpha(this.baseMaskAlpha);
      for (let i = 0; i < drop; i++) {
        const op = this.history[i];
        if (op.type === "mask") this.applyMaskOp(op.op);
      }
      this.baseMaskAlpha = this.snapshotMaskAlpha();
      this.restoreMaskAlpha(live);
    }
    this.history = this.history.slice(drop);
    this.historyIndex = Math.max(0, this.historyIndex - drop);
    const rekeyed = new Map<number, Uint8Array>();
    for (const [k, snap] of this.keyframes) {
      if (k > drop) rekeyed.set(k - drop, snap);
    }
    this.keyframes.clear();
    for (const [k, snap] of rekeyed) this.keyframes.set(k, snap);
  }

  /** Restore the mask to its state after the first `target` ops. */
  private restoreMaskToIndex(target: number): void {
    let from = 0;
    let base = this.baseMaskAlpha;
    for (const [k, snap] of this.keyframes) {
      if (k <= target && k > from) {
        from = k;
        base = snap;
      }
    }
    if (base) this.restoreMaskAlpha(base);
    for (let i = from; i < target; i++) {
      const op = this.history[i];
      if (op.type === "mask") this.applyMaskOp(op.op);
    }
  }

  private applyMaskOp(op: MaskOp): void {
    switch (op.kind) {
      case "stroke": {
        const pts = op.points;
        if (pts.length === 2) {
          this.strokeSegment(pts[0], pts[1], pts[0], pts[1], op.tool, op.size);
          break;
        }
        for (let i = 0; i + 3 < pts.length; i += 2) {
          this.strokeSegment(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], op.tool, op.size);
        }
        break;
      }
      case "bucket":
        this.bucketFill(op.x, op.y);
        break;
      case "magic":
        this.magicFill(op.x, op.y);
        break;
      case "fillAll":
        this.fillMaskCore();
        break;
      case "clear": {
        const ctx = this.mask.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, this.mask.width, this.mask.height);
        break;
      }
    }
  }

  scaleStamp(factor: number): void {
    if (!this.stamp.bitmap) return;
    const before = this.stampSnapshot();
    this.stamp.scale = Math.max(0.05, Math.min(8, this.stamp.scale * factor));
    if (this.stamp.scale !== before.scale) {
      this.pushOp({ type: "stamp", before, after: this.stampSnapshot() });
    }
    this.render();
    this.events.onChange?.();
  }

  resetStamp(): void {
    const { w, h } = this.dims;
    this.stamp.x = w / 2;
    this.stamp.y = h / 2;
    if (this.stamp.bitmap) {
      this.stamp.scale = (w * 0.6) / this.stamp.bitmap.width;
    } else {
      this.stamp.scale = 1;
    }
    this.render();
    this.events.onChange?.();
  }

  clearMask(): void {
    const ctx = this.mask.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, this.mask.width, this.mask.height);
    this.pushOp({ type: "mask", op: { kind: "clear" } });
    this.render();
    this.events.onChange?.();
  }

  fillMask(): void {
    this.fillMaskCore();
    this.pushOp({ type: "mask", op: { kind: "fillAll" } });
    this.render();
    this.events.onChange?.();
  }

  private fillMaskCore(): void {
    const ctx = this.mask.getContext("2d");
    if (!ctx) return;
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "rgba(0, 0, 0, 1)";
    ctx.fillRect(0, 0, this.mask.width, this.mask.height);
    ctx.restore();
  }

  private paintStroke(x0: number, y0: number, x1: number, y1: number): void {
    const tool = this.tool === "eraser" ? "eraser" : "pen";
    this.strokeSegment(x0, y0, x1, y1, tool, this.penSize);
    this.render();
  }

  private strokeSegment(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    tool: "pen" | "eraser",
    size: number
  ): void {
    const ctx = this.mask.getContext("2d");
    if (!ctx) return;
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = size;
    ctx.globalCompositeOperation = tool === "eraser" ? "destination-out" : "source-over";
    ctx.strokeStyle = "rgba(0,0,0,1)";
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
    ctx.restore();
  }

  bucketFillAt(sx: number, sy: number): void {
    if (!this.bucketFill(sx, sy)) return;
    this.pushOp({ type: "mask", op: { kind: "bucket", x: sx, y: sy } });
    this.render();
    this.events.onChange?.();
  }

  private bucketFill(sx: number, sy: number): boolean {
    const ix = Math.floor(sx);
    const iy = Math.floor(sy);
    const w = this.mask.width;
    const h = this.mask.height;
    if (ix < 0 || iy < 0 || ix >= w || iy >= h) return false;

    const ctx = this.mask.getContext("2d");
    if (!ctx) return false;
    const id = ctx.getImageData(0, 0, w, h);
    const a = id.data;

    const start = (iy * w + ix) * 4 + 3;
    if (a[start] > 0) return false; // already filled

    // Span-based flood fill on alpha == 0 pixels.
    const stack: Array<[number, number]> = [[ix, iy]];
    while (stack.length) {
      const [x, y] = stack.pop()!;
      const row = y * w;
      if (a[(row + x) * 4 + 3] !== 0) continue;

      let lx = x;
      while (lx > 0 && a[(row + lx - 1) * 4 + 3] === 0) lx--;
      let rx = x;
      while (rx < w - 1 && a[(row + rx + 1) * 4 + 3] === 0) rx++;

      let spanAbove = false;
      let spanBelow = false;
      for (let i = lx; i <= rx; i++) {
        a[(row + i) * 4 + 3] = 255;

        if (y > 0) {
          const aboveAlpha = a[((y - 1) * w + i) * 4 + 3];
          if (aboveAlpha === 0 && !spanAbove) {
            stack.push([i, y - 1]);
            spanAbove = true;
          } else if (aboveAlpha !== 0) {
            spanAbove = false;
          }
        }
        if (y < h - 1) {
          const belowAlpha = a[((y + 1) * w + i) * 4 + 3];
          if (belowAlpha === 0 && !spanBelow) {
            stack.push([i, y + 1]);
            spanBelow = true;
          } else if (belowAlpha !== 0) {
            spanBelow = false;
          }
        }
      }
    }

    ctx.putImageData(id, 0, 0);
    return true;
  }

  magicFillAt(sx: number, sy: number): void {
    if (!this.magicFill(sx, sy)) return;
    this.pushOp({ type: "mask", op: { kind: "magic", x: sx, y: sy } });
    this.render();
    this.events.onChange?.();
  }

  /**
   * Magic wand: flood-fills the mask over the contiguous region of the source
   * image whose color is within MAGIC_TOLERANCE of the clicked pixel.
   */
  private magicFill(sx: number, sy: number): boolean {
    if (!this.source) return false;
    const ix = Math.floor(sx);
    const iy = Math.floor(sy);
    const w = this.mask.width;
    const h = this.mask.height;
    if (ix < 0 || iy < 0 || ix >= w || iy >= h) return false;

    const sctx = this.source.getContext("2d");
    const mctx = this.mask.getContext("2d");
    if (!sctx || !mctx) return false;
    const src = sctx.getImageData(0, 0, w, h).data;
    const mid = mctx.getImageData(0, 0, w, h);
    const md = mid.data;

    const seed = (iy * w + ix) * 4;
    const r = src[seed];
    const g = src[seed + 1];
    const b = src[seed + 2];
    const visited = new Uint8Array(w * h);
    const ok = (i: number): boolean => {
      if (visited[i]) return false;
      const p = i * 4;
      const dr = src[p] - r;
      const dg = src[p + 1] - g;
      const db = src[p + 2] - b;
      return dr * dr + dg * dg + db * db <= MAGIC_TOLERANCE_SQ;
    };

    let changed = false;
    const stack: Array<[number, number]> = [[ix, iy]];
    while (stack.length) {
      const [x, y] = stack.pop()!;
      const row = y * w;
      if (!ok(row + x)) continue;

      let lx = x;
      while (lx > 0 && ok(row + lx - 1)) lx--;
      let rx = x;
      while (rx < w - 1 && ok(row + rx + 1)) rx++;

      let spanAbove = false;
      let spanBelow = false;
      for (let i = lx; i <= rx; i++) {
        const idx = row + i;
        visited[idx] = 1;
        const p = idx * 4;
        if (md[p + 3] !== 255) changed = true;
        md[p] = 0;
        md[p + 1] = 0;
        md[p + 2] = 0;
        md[p + 3] = 255;

        if (y > 0) {
          const above = ok((y - 1) * w + i);
          if (above && !spanAbove) {
            stack.push([i, y - 1]);
            spanAbove = true;
          } else if (!above) {
            spanAbove = false;
          }
        }
        if (y < h - 1) {
          const below = ok((y + 1) * w + i);
          if (below && !spanBelow) {
            stack.push([i, y + 1]);
            spanBelow = true;
          } else if (!below) {
            spanBelow = false;
          }
        }
      }
    }

    if (changed) mctx.putImageData(mid, 0, 0);
    return changed;
  }

  private hitTestStamp(x: number, y: number): boolean {
    if (!this.stamp.bitmap || !this.stamp.visible) return false;
    const sw = this.stamp.bitmap.width * this.stamp.scale;
    const sh = this.stamp.bitmap.height * this.stamp.scale;
    const sx = this.stamp.x - sw / 2;
    const sy = this.stamp.y - sh / 2;
    return x >= sx && x <= sx + sw && y >= sy && y <= sy + sh;
  }

  private installPointerHandlers(): void {
    const canvas = this.previewCanvas;

    const toOutputCoords = (e: PointerEvent): { x: number; y: number } => {
      const rect = canvas.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
      const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
      return { x, y };
    };

    canvas.addEventListener("pointerdown", (e) => {
      if (!this.source) return;
      if (this.tool === "hand") return;
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      const p = toOutputCoords(e);
      if (this.hitTestStamp(p.x, p.y)) {
        this.dragging = "stamp";
        this.stampMoved = false;
        this.stampDragBefore = this.stampSnapshot();
        this.dragOffset = { x: p.x - this.stamp.x, y: p.y - this.stamp.y };
        return;
      }
      if (this.tool === "bucket") {
        this.dragging = null;
        this.bucketFillAt(p.x, p.y);
        return;
      }
      if (this.tool === "magic") {
        this.dragging = null;
        this.magicFillAt(p.x, p.y);
        return;
      }
      if (this.lineMode || e.shiftKey) {
        this.dragging = "line";
        const a = this.lineAnchor ?? p;
        this.pendingLine = {
          x0: a.x,
          y0: a.y,
          x1: p.x,
          y1: p.y,
          tool: this.tool === "eraser" ? "eraser" : "pen",
        };
        this.render();
        return;
      }
      this.dragging = "paint";
      this.prevPointer = p;
      this.activeStroke = {
        tool: this.tool === "eraser" ? "eraser" : "pen",
        size: this.penSize,
        points: [p.x, p.y],
      };
      this.paintStroke(p.x, p.y, p.x, p.y);
    });

    canvas.addEventListener("pointermove", (e) => {
      if (!this.dragging) {
        // Rubber-band preview from the chain anchor to the hovering cursor.
        const lineActive =
          (this.lineMode || e.shiftKey) && (this.tool === "pen" || this.tool === "eraser");
        if (lineActive && this.lineAnchor && this.source) {
          const p = toOutputCoords(e);
          this.pendingLine = {
            x0: this.lineAnchor.x,
            y0: this.lineAnchor.y,
            x1: p.x,
            y1: p.y,
            tool: this.tool === "eraser" ? "eraser" : "pen",
          };
          this.render();
        } else if (this.pendingLine) {
          this.pendingLine = null;
          this.render();
        }
        return;
      }
      const p = toOutputCoords(e);
      if (this.dragging === "stamp") {
        this.stamp.x = p.x - this.dragOffset.x;
        this.stamp.y = p.y - this.dragOffset.y;
        this.stampMoved = true;
        this.render();
      } else if (this.dragging === "line") {
        if (this.pendingLine) {
          this.pendingLine.x1 = p.x;
          this.pendingLine.y1 = p.y;
          this.render();
        }
      } else {
        if (this.prevPointer) this.paintStroke(this.prevPointer.x, this.prevPointer.y, p.x, p.y);
        this.prevPointer = p;
        this.activeStroke?.points.push(p.x, p.y);
      }
    });

    const end = (e: PointerEvent): void => {
      if (!this.dragging) return;
      const mode = this.dragging;
      const moved = this.stampMoved;
      const stroke = this.activeStroke;
      const line = this.pendingLine;
      const stampBefore = this.stampDragBefore;
      this.dragging = null;
      this.prevPointer = null;
      this.stampMoved = false;
      this.activeStroke = null;
      this.pendingLine = null;
      this.stampDragBefore = null;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      if (mode === "paint" && stroke) {
        this.pushOp({ type: "mask", op: { kind: "stroke", ...stroke } });
        // Shift+click chains from where the last freehand stroke ended.
        const pts = stroke.points;
        this.lineAnchor = { x: pts[pts.length - 2], y: pts[pts.length - 1] };
      }
      if (mode === "line" && line) {
        if (e.type === "pointercancel") {
          this.render();
          return;
        }
        const size = this.penSize;
        this.strokeSegment(line.x0, line.y0, line.x1, line.y1, line.tool, size);
        this.pushOp({
          type: "mask",
          op: { kind: "stroke", tool: line.tool, size, points: [line.x0, line.y0, line.x1, line.y1] },
        });
        this.lineAnchor = { x: line.x1, y: line.y1 };
      }
      if (mode === "stamp" && moved && stampBefore) {
        this.pushOp({ type: "stamp", before: stampBefore, after: this.stampSnapshot() });
      }
      if (mode === "paint" || mode === "line" || (mode === "stamp" && moved)) {
        this.render();
        this.events.onChange?.();
      }
    };
    canvas.addEventListener("pointerup", end);
    canvas.addEventListener("pointercancel", end);

    canvas.addEventListener("pointerleave", () => {
      if (!this.dragging && this.pendingLine) {
        this.pendingLine = null;
        this.render();
      }
    });
  }

  private getBackdropPattern(ctx: CanvasRenderingContext2D): CanvasPattern | null {
    if (this.backdropPattern) return this.backdropPattern;
    const tile = document.createElement("canvas");
    tile.width = 32;
    tile.height = 32;
    const tctx = tile.getContext("2d");
    if (!tctx) return null;
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, 32, 32);
    tctx.fillStyle = "#e6e6e6";
    tctx.fillRect(0, 0, 16, 16);
    tctx.fillRect(16, 16, 16, 16);
    this.backdropPattern = ctx.createPattern(tile, "repeat");
    return this.backdropPattern;
  }

  render(): void {
    const ctx = this.previewCanvas.getContext("2d");
    if (!ctx) return;
    const { w, h } = this.dims;
    ctx.clearRect(0, 0, w, h);

    if (this.source) {
      ctx.fillStyle = this.getBackdropPattern(ctx) ?? "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(this.source, 0, 0);
    } else {
      ctx.fillStyle = "#f7f7f7";
      ctx.fillRect(0, 0, w, h);
    }

    if (this.maskVisible) {
      const octx = this.overlay.getContext("2d");
      if (octx) {
        octx.save();
        octx.globalCompositeOperation = "copy";
        octx.drawImage(this.mask, 0, 0);
        octx.globalCompositeOperation = "source-in";
        octx.fillStyle = MASK_OVERLAY_COLOR;
        octx.fillRect(0, 0, w, h);
        octx.restore();
        ctx.drawImage(this.overlay, 0, 0);
      }
    }

    if (this.stamp.bitmap && this.stamp.visible) {
      const sw = this.stamp.bitmap.width * this.stamp.scale;
      const sh = this.stamp.bitmap.height * this.stamp.scale;
      const sx = this.stamp.x - sw / 2;
      const sy = this.stamp.y - sh / 2;
      ctx.drawImage(this.stamp.bitmap, sx, sy, sw, sh);
      ctx.save();
      ctx.strokeStyle = "rgba(0,0,0,0.4)";
      ctx.lineWidth = 3;
      ctx.setLineDash([12, 12]);
      ctx.strokeRect(sx, sy, sw, sh);
      ctx.restore();
    }

    if (this.pendingLine) {
      const { x0, y0, x1, y1, tool } = this.pendingLine;
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineWidth = this.penSize;
      if (tool === "eraser") {
        ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
        ctx.setLineDash([18, 14]);
      } else {
        ctx.strokeStyle = MASK_OVERLAY_COLOR;
      }
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
      ctx.restore();
    } else if (this.lineMode && this.lineAnchor) {
      // Mark where the next tap will chain from (no hover cue on touch).
      ctx.save();
      ctx.strokeStyle = MASK_OVERLAY_COLOR;
      ctx.lineWidth = 4;
      ctx.setLineDash([8, 8]);
      ctx.beginPath();
      ctx.arc(this.lineAnchor.x, this.lineAnchor.y, Math.max(this.penSize / 2, 14), 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  bake(): BakeResult {
    const { w, h } = this.dims;

    const composed = document.createElement("canvas");
    composed.width = w;
    composed.height = h;
    const cctx = composed.getContext("2d");
    if (!cctx) throw new Error("2d context unavailable");
    if (this.source) cctx.drawImage(this.source, 0, 0);
    if (this.stamp.bitmap && this.stamp.visible) {
      const sw = this.stamp.bitmap.width * this.stamp.scale;
      const sh = this.stamp.bitmap.height * this.stamp.scale;
      cctx.drawImage(this.stamp.bitmap, this.stamp.x - sw / 2, this.stamp.y - sh / 2, sw, sh);
    }
    const composedData = cctx.getImageData(0, 0, w, h);

    const protectCanvas = document.createElement("canvas");
    protectCanvas.width = w;
    protectCanvas.height = h;
    const pctx = protectCanvas.getContext("2d");
    if (!pctx) throw new Error("2d context unavailable");
    pctx.drawImage(this.mask, 0, 0);
    if (this.stamp.bitmap && this.stamp.visible) {
      const sw = this.stamp.bitmap.width * this.stamp.scale;
      const sh = this.stamp.bitmap.height * this.stamp.scale;
      pctx.drawImage(this.stamp.bitmap, this.stamp.x - sw / 2, this.stamp.y - sh / 2, sw, sh);
    }
    const protectData = pctx.getImageData(0, 0, w, h);
    const protectedAlpha = new Uint8Array(w * h);
    for (let i = 0; i < protectedAlpha.length; i++) {
      protectedAlpha[i] = protectData.data[i * 4 + 3];
    }

    return { rgba: composedData.data, protectedAlpha, width: w, height: h };
  }
}
